import { useEffect, useState } from "react";
import axios from "axios";
import { X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LEAD_SUBMITTED_KEY = "heylpmate_homepage_lead_submitted";
const LEAD_IMAGE_URL =
  "https://customer-assets-eiarnc6j.emergentagent.net/wingman/05a3193c-9053-43cf-b79c-afd2faf51841/attachments/89f2e33f02c5497786e1824883aa9b54_IMG-20260920-WA0002-1-.jpg";

/**
 * SplashLead — full-screen lead-capture splash shown on every visit until the
 * visitor submits the form. Uses localStorage so it persists across sessions.
 * Displays AFTER the short animated brand SplashScreen has dismissed.
 */
export default function SplashLead() {
  const [show, setShow] = useState(false);
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [thanks, setThanks] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Only show on the homepage
    if (window.location.pathname !== "/") return;
    const submitted = localStorage.getItem(LEAD_SUBMITTED_KEY) === "1";
    if (submitted) return;
    // Delay so brand SplashScreen finishes first
    const t = setTimeout(() => setShow(true), 2100);
    return () => clearTimeout(t);
  }, []);

  const dismiss = () => setShow(false);

  const validMobile = (v) => {
    const digits = (v || "").replace(/\D/g, "");
    return /^[6-9]\d{9}$/.test(digits);
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!name.trim() || name.trim().length < 2) return setErr("Please enter your full name.");
    if (!validMobile(mobile)) return setErr("Enter a valid 10-digit Indian mobile number.");
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return setErr("Enter a valid email address.");
    setBusy(true);
    try {
      await axios.post(`${API}/leads/homepage`, {
        name: name.trim(),
        mobile: mobile.replace(/\D/g, ""),
        email: email.trim().toLowerCase(),
      });
      localStorage.setItem(LEAD_SUBMITTED_KEY, "1");
      setThanks(true);
      setTimeout(() => setShow(false), 2000);
    } catch (er) {
      setErr(er?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (!show) return null;

  return (
    <div
      data-testid="splash-lead"
      className="fixed inset-0 z-[9998] overflow-y-auto"
      style={{
        background: "linear-gradient(160deg, #F0F7FF 0%, #E8F5EE 55%, #FFFFFF 100%)",
      }}
    >
      <button
        data-testid="splash-lead-close"
        onClick={dismiss}
        aria-label="Skip"
        className="absolute top-4 right-4 md:top-6 md:right-6 w-9 h-9 rounded-full grid place-items-center bg-white/70 backdrop-blur border border-black/10 hover:bg-white transition"
      >
        <X className="w-4 h-4 text-neutral-600" />
      </button>

      <div className="min-h-screen flex flex-col items-center justify-start md:justify-center px-5 py-8 md:py-12">
        <img
          src={LEAD_IMAGE_URL}
          alt="heylpMATE welcome"
          data-testid="splash-lead-image"
          className="w-full max-w-2xl rounded-3xl shadow-xl object-cover hm-lead-pulse"
          style={{ maxHeight: "44vh" }}
        />

        <div className="text-center mt-6 md:mt-8 max-w-2xl">
          <h1 className="font-display font-bold text-3xl sm:text-4xl md:text-5xl leading-tight" style={{ color: "#00604A" }}>
            Helping is a Mindset. <span style={{ color: "#10B981" }}>We like you already.</span>
          </h1>
          <p className="mt-3 text-sm md:text-base text-neutral-600">
            Let us stay in touch — takes 10 seconds.
          </p>
        </div>

        {thanks ? (
          <div data-testid="splash-lead-thanks" className="mt-8 max-w-lg w-full text-center rounded-3xl border border-emerald-200 bg-white/80 backdrop-blur p-6 shadow">
            <div className="font-display text-2xl" style={{ color: "#10B981" }}>Thank you!</div>
            <p className="text-sm text-neutral-700 mt-2">
              A HeylpMATE will be in touch soon — refer and get one free help.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 md:mt-8 w-full max-w-lg space-y-3">
            <input
              data-testid="splash-lead-name"
              type="text"
              placeholder="Full Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="w-full rounded-2xl border border-black/10 bg-white/90 backdrop-blur px-5 py-3.5 text-base focus:outline-none focus:ring-2 focus:ring-[#10B981]"
            />
            <input
              data-testid="splash-lead-mobile"
              type="tel"
              inputMode="numeric"
              placeholder="Mobile Number (10 digits)"
              value={mobile}
              onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
              required
              className="w-full rounded-2xl border border-black/10 bg-white/90 backdrop-blur px-5 py-3.5 text-base focus:outline-none focus:ring-2 focus:ring-[#10B981]"
            />
            <input
              data-testid="splash-lead-email"
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full rounded-2xl border border-black/10 bg-white/90 backdrop-blur px-5 py-3.5 text-base focus:outline-none focus:ring-2 focus:ring-[#10B981]"
            />
            {err && (
              <div data-testid="splash-lead-error" className="text-sm text-red-600 text-center">{err}</div>
            )}
            <button
              type="submit"
              data-testid="splash-lead-submit"
              disabled={busy}
              className="w-full rounded-2xl px-5 py-3.5 text-white font-bold text-base shadow-lg transition disabled:opacity-70"
              style={{ background: "linear-gradient(135deg, #10B981, #00604A)" }}
            >
              {busy ? "Submitting…" : "Yes, Stay in Touch."}
            </button>
            <button
              type="button"
              data-testid="splash-lead-skip"
              onClick={dismiss}
              className="w-full text-center text-sm text-neutral-500 hover:text-neutral-800 underline underline-offset-4 mt-1"
            >
              Skip for now
            </button>
          </form>
        )}
      </div>

      <style>{`
        @keyframes hmLeadPulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.82; transform: scale(0.985); }
        }
        .hm-lead-pulse {
          animation: hmLeadPulse 3.2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
